import { i18n } from '@/i18n';
import { InstanceAccountType, Role } from '@sysarb/sysarb-types/service-base/enums';

export type RoleDetails = {
    bypassOrganisation: boolean;
    legalResponsibility: boolean;
    icon: string;
    label: string;
};

type UseRoleReturn = {
    getRoleTranslation(role?: Role): string;
    getRoleIcon(role: Role): string;
    getRoleDetails(role?: Role | null, instanceAccountType?: InstanceAccountType | null): RoleDetails | null;
};

export function useRole(): UseRoleReturn {
    function getRoleIcon(value: Role | 'developer'): string {
        switch (value) {
            case Role.Manager:
            case Role.ManagerDeputy:
                return 'BriefcaseBusiness';
            case Role.SectionManager:
                return 'Network';
            case Role.OperationalAdministrator:
                return 'FilePenLine';
            case Role.Administrator:
                return 'UserCog';
            case Role.SystemAdministrator:
                return 'Crown';
            case Role.Root:
                return 'Banana';
            case Role.ReadOnly:
                return 'FileSearch';
            case Role.Union:
                return 'Handshake';
            case Role.SalarySurveyAnalyst:
                return 'HandHelping';
            default:
                return 'User';
        }
    }

    function getRoleTranslation(role?: Role | 'developer'): string {
        switch (role) {
            case Role.Administrator:
                return i18n.global.t('system.roles.administrator');
            case Role.Employee:
                return i18n.global.t('system.roles.employee');
            case Role.Manager:
                return i18n.global.t('system.roles.manager');
            case Role.Consultant:
                return i18n.global.t('system.roles.consultant');
            case Role.Union:
                return i18n.global.t('system.roles.union');
            case Role.ManagerDeputy:
                return i18n.global.t('system.roles.sectionManager');
            case Role.OperationalAdministrator:
                return i18n.global.t('system.roles.operationalAdministrator');
            case Role.ReadOnly:
                return i18n.global.t('system.roles.readOnly');
            case Role.Root:
                return i18n.global.t('system.roles.root');
            case Role.SalarySurveyAnalyst:
                return i18n.global.t('system.roles.salarySurveyAnalyst');
            case Role.SectionManager:
                return i18n.global.t('system.roles.sectionManager');
            case Role.SystemAdministrator:
                return i18n.global.t('system.roles.support');
            default:
                return '';
        }
    }

    /**
     * Returns details of the selected role.
     * @param role The role to get details of.
     */
    function getRoleDetails(role?: Role | null, instanceAccountType?: InstanceAccountType | null): RoleDetails | null {
        if (instanceAccountType != null && (role === Role.Root || role === Role.SystemAdministrator || role === Role.Consultant)) {
            switch (instanceAccountType) {
                case InstanceAccountType.Consultant: return { bypassOrganisation: true, legalResponsibility: false, label:getRoleTranslation(Role.Consultant), icon: getRoleIcon(Role.Consultant) };
                case InstanceAccountType.Developer: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation('developer'), icon: getRoleIcon('developer') };
                case InstanceAccountType.Root: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation(Role.Root), icon: getRoleIcon(Role.Root) };
                case InstanceAccountType.Support: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation(Role.SystemAdministrator), icon: getRoleIcon(Role.SystemAdministrator) };
                default: return null;
            }
        }

        switch (role) {
            case Role.Root: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation(Role.Root), icon: getRoleIcon(Role.Root) };
            case Role.SystemAdministrator: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation(Role.SystemAdministrator), icon: getRoleIcon(Role.SystemAdministrator) };
            case Role.Consultant: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation(Role.Consultant), icon: getRoleIcon(Role.Consultant) };
            case Role.Employee: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.Employee), icon: getRoleIcon(Role.Employee) };
            case Role.Manager: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.Manager), icon: getRoleIcon(Role.Manager) };
            case Role.SectionManager: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.SectionManager), icon: getRoleIcon(Role.SectionManager) };
            case Role.OperationalAdministrator: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.OperationalAdministrator), icon: getRoleIcon(Role.OperationalAdministrator) };
            case Role.Administrator: return { bypassOrganisation: true, legalResponsibility: false, label: getRoleTranslation(Role.Administrator), icon: getRoleIcon(Role.Administrator) };
            case Role.ReadOnly: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.ReadOnly), icon: getRoleIcon(Role.ReadOnly) };
            case Role.Union: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.Union), icon: getRoleIcon(Role.Union) };
            case Role.SalarySurveyAnalyst: return { bypassOrganisation: false, legalResponsibility: false, label: getRoleTranslation(Role.SalarySurveyAnalyst), icon: getRoleIcon(Role.SalarySurveyAnalyst) };
            default: return null;
        }
    }

    return {
        getRoleTranslation,
        getRoleIcon,
        getRoleDetails
    };
}
